import { isDesktopRuntime } from './desktopDetection';

/**
 * I-2572 (MS-3): a session that ended other than by the Sign out dialog is written to the desktop
 * app's log, through one command (`report_session_end`,
 * `desktop/src-tauri/src/diagnostics/session_end.rs`), so the next "why was I signed out?" is
 * answered from the log. Jay's sign-out of 11-03 left no line at all.
 *
 * ⛔ Words only, from closed vocabularies (`SESSION_END_PATHS`, `SESSION_END_REASONS`): never a token,
 * an address or a server's text. The app keeps only plain lower-case words of each field.
 *
 * A leaf, like `desktopSessionVault`: `ApiService` calls it, so it must not import anything that
 * imports `ApiService`.
 */
export interface ISessionEndReport {
  /** Where the end came from (`SESSION_END_PATHS`). */
  path: string;
  /** Why (`SESSION_END_REASONS`, `SESSION_END_LOCAL_REASONS`). */
  reason: string;
  /** The web's cause (`SESSION_END_CAUSES`). */
  cause: string;
  /** What happened to the keychain's copy of the session. */
  keychain: 'cleared' | 'kept';
}

type SendType = (report: ISessionEndReport) => Promise<unknown>;

async function sendToDesktop(report: ISessionEndReport): Promise<unknown> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke('report_session_end', { report });
}

/** Writes the line in the desktop app; nothing elsewhere. A send that fails is dropped. */
export function logSessionEnd(report: ISessionEndReport, send: SendType = sendToDesktop): void {
  if (!isDesktopRuntime()) return;
  try {
    void send(report).catch(() => undefined);
  } catch {
    // Logging a sign-out must never be the reason a sign-out fails.
  }
}
